import { Controller } from "@hotwired/stimulus"

/**
 * Handles the auto-hiding behavior of the website header on scroll.
 * The header hides when scrolling down and shows when scrolling up or near the top.
 * Uses requestAnimationFrame for performance and accessibility attributes for screen readers.
 */
export default class extends Controller {
  static classes = ["shown", "hidden"]
  static values = {
    // Distance to scroll down before hiding.
    hideThreshold: { type: Number, default: 50 },
    // Distance to scroll up before showing.
    showThreshold: { type: Number, default: 25 },
    // Scroll position near top where header is always shown.
    topThreshold: { type: Number, default: 32 }
  }

  connect() {
    this.syncVisibleState()
    this.lastScrollY = this.scrollY
    this.direction = null
    this.directionStartY = this.lastScrollY
    this.rafId = null
    this.boundOnScroll = this.onScroll.bind(this)

    window.addEventListener("scroll", this.boundOnScroll, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this.boundOnScroll)

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  /**
   * Throttles scroll events to one DOM update per frame.
   */
  onScroll() {
    if (this.rafId) {
      return
    }

    // rAF keeps the scroll work to one DOM update per frame.
    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null
      this.updateVisibility()
    })
  }

  /**
   * Calculates scroll direction and distance to determine header visibility.
   */
  updateVisibility() {
    const currentScrollY = this.scrollY

    if (currentScrollY <= this.topThresholdValue) {
      this.show()
      this.resetMotionState(currentScrollY)
      return
    }

    if (currentScrollY === this.lastScrollY) {
      return
    }

    const direction = currentScrollY > this.lastScrollY ? "down" : "up"

    if (direction !== this.direction) {
      this.direction = direction
      this.directionStartY = this.lastScrollY
    }

    const distanceFromStart =
      direction === "down" ? currentScrollY - this.directionStartY : this.directionStartY - currentScrollY

    if (!this.isHidden && direction === "down" && distanceFromStart >= this.hideThresholdValue) {
      this.hide()
      this.directionStartY = currentScrollY
    } else if (this.isHidden && direction === "up" && distanceFromStart >= this.showThresholdValue) {
      this.show()
      this.directionStartY = currentScrollY
    }

    this.lastScrollY = currentScrollY
  }

  /**
   * Hides the header with CSS classes and ARIA attributes.
   */
  hide() {
    this.setHidden(true)
  }

  /**
   * Shows the header with CSS classes and ARIA attributes.
   */
  show() {
    this.setHidden(false)
  }

  /**
   * Resets internal tracking state.
   */
  resetMotionState(scrollY) {
    this.direction = null
    this.directionStartY = scrollY
    this.lastScrollY = scrollY
  }

  /**
   * Initializes the header to a visible state.
   */
  syncVisibleState() {
    this.isHidden = false
    this.element.classList.add(this.shownClass)
    this.element.classList.remove(this.hiddenClass)
    this.element.removeAttribute("aria-hidden")
    this.element.removeAttribute("inert")
  }

  /**
   * Toggles visibility state and updates DOM.
   */
  setHidden(hidden) {
    if (this.isHidden === hidden) {
      return
    }

    this.element.classList.toggle(this.shownClass, !hidden)
    this.element.classList.toggle(this.hiddenClass, hidden)
    this.element.toggleAttribute("aria-hidden", hidden)
    this.element.toggleAttribute("inert", hidden)
    this.isHidden = hidden
  }

  /**
   * Cross-browser normalized scroll position.
   */
  get scrollY() {
    return window.scrollY || document.documentElement.scrollTop || 0
  }
}
